'use client'

import { SparklesIcon, X } from 'lucide-react'
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AIGeneratorProvider } from './modals/ai-generator-v2/context'
import { EditMode } from './modals/ai-generator-v2/edit-mode'
import { PreviewMode } from './modals/ai-generator-v2/preview-mode'
import { useAIGenerator } from './modals/ai-generator-v2/context'
import { useTranslations } from 'next-intl'

interface AIGeneratorPopupProps {
  onImageSelect: (imageUrl: string) => void
  onClose: () => void
  onGenerationStart?: (count: number) => void
  onGenerationEnd?: () => void
  size?: 'default' | 'large'
}

function AIGeneratorContent({
  size = 'default',
}: {
  size?: 'default' | 'large'
}) {
  const { isEditing } = useAIGenerator()
  const t = useTranslations('modals.ai-generator-v2')

  const maxWidth = size === 'large' ? 'sm:max-w-[900px]' : 'sm:max-w-[640px]'

  return (
    <div
      className={`grid w-full gap-4 border dialog-bg shadow-lg duration-200 animate-in fade-in-0 zoom-in-95 sm:rounded-lg ${maxWidth} p-6 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-gray-200/50 dark:border-gray-800/50`}
    >
      <DialogHeader className="space-y-3">
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-brand_2" />
          <DialogTitle className="text-xl font-semibold">
            {t('title')}
          </DialogTitle>
        </div>
        <DialogDescription>{t('description')}</DialogDescription>
      </DialogHeader>

      {isEditing ? <EditMode /> : <PreviewMode />}
    </div>
  )
}

export function AIGeneratorPopup({
  onImageSelect,
  onClose,
  onGenerationStart,
  onGenerationEnd,
  size = 'default',
}: AIGeneratorPopupProps) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div className="relative">
        {/* Close button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="absolute -top-2 -right-2 z-10 w-8 h-8 bg-white rounded-full text-gray-600 hover:bg-gray-100 shadow-lg"
        >
          <X className="h-4 w-4" />
        </Button>

        <AIGeneratorProvider
          onAddToLibraryComplete={onClose}
          onGenerationStart={onGenerationStart}
          onGenerationEnd={onGenerationEnd}
        >
          <AIGeneratorContent size={size} />
        </AIGeneratorProvider>
      </div>
    </div>
  )
}
